import {
  Ticket,
  Mail,
  LayoutDashboard,
  Users,
  Brain,
  GraduationCap,
  BookOpen,
  ShieldAlert,
  FileText,
  Settings,
  Crown,
  UserCheck,
  Layers,
  Award,
  Route,
  Network,
  Activity,
  TrendingUp,
  Building2,
  CalendarDays,
  Handshake,
  BarChart3,
  Compass,
  AlertTriangle,
  Target,
  ShieldCheck,
  Wrench,
  CreditCard,
  Receipt,
  RefreshCw,
  Headset,
  MessageSquare,
  Globe,
  Users2,
  UserCog,
  Rocket,
  ClipboardList,
  Trophy,
  Kanban,
  LayoutTemplate,
  HelpCircle,
  ArrowRightLeft,
  Video,
  FlaskConical,
  MapPin,
} from "lucide-react";

interface NavChild {
  to: string;
  label: string;
  icon: React.ElementType;
  module?: string;
}

export interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  children?: NavChild[];
  adminOnly?: boolean;
  workspace?: string;
  module?: string;
}

export const getNavItems = (teamLabel: string): NavItem[] => [
  { to: "/launch", icon: Rocket, label: "Launch MemberOS" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", workspace: "executive_director" },
  {
    to: "/member-success",
    icon: Users,
    label: "Member Success",
    workspace: "membership_pro",
    module: "membership",
    children: [
      { to: "/member-success", icon: Users, label: "Overview", module: "membership" },
      { to: "/crm", icon: Headset, label: "Prospects" },
      { to: "/members", icon: Users, label: "Members", module: "membership" },
      { to: "/companies", icon: Building2, label: "Companies", module: "membership" },
      { to: "/staff", icon: UserCog, label: "Staff" },
    ],
  },
  {
    to: "/programs",
    icon: GraduationCap,
    label: "Programming",
    workspace: "event_planner",
    children: [
      { to: "/programming-calendar", icon: Kanban, label: "Command Center", module: "programming_calendar" },
      { to: "/events", icon: CalendarDays, label: "Events", module: "events" },
      { to: "/signature-events", icon: Trophy, label: "Signature Events", module: "events" },
      { to: "/playbooks", icon: LayoutTemplate, label: "Playbooks", module: "programming_calendar" },
      { to: "/programs", icon: GraduationCap, label: "Programs", module: "programs" },
      { to: "/learning", icon: BookOpen, label: "Learning", module: "programs" },
      { to: "/video-library", icon: Video, label: "Video Library", module: "video_library" },
      { to: "/learning-hub", icon: GraduationCap, label: "Learning Hub", module: "video_library" },
      { to: "/meeting-ops", icon: ClipboardList, label: "Meeting Ops" },
      { to: "/locations", icon: MapPin, label: "Locations" },
    ],
  },
  {
    to: "/communications",
    icon: Mail,
    label: "Communications",
    workspace: "marketing_comms",
    module: "communications_pro",
  },
  { to: "/community", icon: MessageSquare, label: "Community", workspace: "marketing_comms", module: "community" },
  { to: "/committees", icon: Users2, label: teamLabel },
  {
    to: "/intelligence",
    icon: Brain,
    label: "Intelligence",
    workspace: "executive_director",
    module: "executive_intelligence",
  },
  {
    to: "/retention",
    icon: ShieldAlert,
    label: "Retention",
    workspace: "membership_pro",
    module: "membership",
  },
  {
    to: "/billing",
    icon: CreditCard,
    label: "Revenue",
    workspace: "executive_director",
    children: [
      { to: "/plans-billing", icon: Layers, label: "Plans & Tiers", module: "membership" },
      { to: "/billing", icon: Receipt, label: "Billing" },
      { to: "/sponsorship", icon: Handshake, label: "Sponsorship", module: "sponsors" },
    ],
  },
  {
    to: "/reports",
    icon: FileText,
    label: "Reports",
    workspace: "executive_director",
    module: "executive_intelligence",
  },
  {
    to: "/website",
    icon: Globe,
    label: "Sites",
    workspace: "marketing_comms",
    children: [
      { to: "/website", icon: Globe, label: "Website", module: "website_pro" },
      { to: "/funnels", icon: Rocket, label: "Campaign Funnels", module: "campaign_funnels" },
      { to: "/response-studio", icon: ClipboardList, label: "Response Studio", module: "response_studio" },
    ],
  },
  { to: "/exchange", icon: ArrowRightLeft, label: "Exchange" },
  { to: "/support", icon: Headset, label: "Support", module: "support_ops" },
  { to: "/plans-billing", icon: Crown, label: "Plans & Billing", adminOnly: true },
  { to: "/settings", icon: Wrench, label: "Settings" },
];

export const getMasterAdminNavItems = (): NavItem[] => [
  { to: "/master-admin", icon: Activity, label: "Platform Overview" },
  {
    to: "/master-admin/ops",
    icon: Building2,
    label: "Platform Operations",
    children: [
      { to: "/master-admin/associations", icon: Network, label: "Associations" },
      { to: "/master-admin/organizations", icon: Building2, label: "Organizations" },
      { to: "/master-admin/trials", icon: FlaskConical, label: "Trials" },
      { to: "/master-admin/provisioning", icon: Rocket, label: "Provisioning" },
      { to: "/master-admin/hierarchy", icon: Layers, label: "Hierarchy Map" },
      { to: "/master-admin/exchange", icon: ArrowRightLeft, label: "Exchange" },
    ],
  },
  {
    to: "/master-admin/billing",
    icon: CreditCard,
    label: "Commercial Ops",
    children: [
      { to: "/master-admin/billing", icon: CreditCard, label: "Billing Command Center" },
      { to: "/master-admin/billing/packaging", icon: Layers, label: "Plans & Packaging" },
      { to: "/master-admin/billing/subscriptions", icon: Receipt, label: "Subscriptions" },
      { to: "/master-admin/billing/invoices", icon: Receipt, label: "Invoices" },
      { to: "/master-admin/billing/entitlements", icon: ShieldCheck, label: "Entitlements" },
      { to: "/master-admin/billing/exceptions", icon: AlertTriangle, label: "Overrides & Exceptions" },
      { to: "/master-admin/billing/resolver", icon: Target, label: "Resolver Inspector" },
      { to: "/master-admin/billing/proposals", icon: FileText, label: "Sales Proposals" },
    ],
  },
  {
    to: "/master-admin/access",
    icon: ShieldCheck,
    label: "Identity & Access",
    children: [
      { to: "/master-admin/access/roles", icon: Crown, label: "Platform Roles" },
      { to: "/master-admin/access/invitations", icon: Users, label: "Invitations" },
      { to: "/master-admin/access/permissions", icon: ShieldAlert, label: "Permission Matrix" },
      { to: "/master-admin/access/logs", icon: FileText, label: "Access Logs" },
    ],
  },
  {
    to: "/master-admin/intelligence",
    icon: BarChart3,
    label: "Intelligence",
    children: [
      { to: "/master-admin/intelligence/reports", icon: FileText, label: "Reports" },
      { to: "/master-admin/intelligence/analytics", icon: Brain, label: "Analytics" },
      { to: "/master-admin/intelligence/adoption", icon: TrendingUp, label: "Adoption" },
      { to: "/master-admin/intelligence/engagement", icon: Activity, label: "Engagement" },
      { to: "/master-admin/intelligence/renewal", icon: RefreshCw, label: "Renewal Risk" },
      { to: "/master-admin/intelligence/revenue", icon: CreditCard, label: "Revenue Health" },
    ],
  },
  {
    to: "/master-admin/reliability",
    icon: Headset,
    label: "Support & Reliability",
    children: [
      { to: "/master-admin/reliability/support", icon: Headset, label: "Attention Queue" },
      { to: "/master-admin/reliability/provisioning", icon: AlertTriangle, label: "Provisioning Failures" },
      { to: "/master-admin/reliability/webhooks", icon: Globe, label: "Webhook Monitor" },
      { to: "/master-admin/reliability/sync", icon: RefreshCw, label: "Sync Health" },
      { to: "/master-admin/reliability/alerts", icon: AlertTriangle, label: "Alerts & Incidents" },
    ],
  },
  {
    to: "/master-admin/governance",
    icon: Settings,
    label: "Governance",
    children: [
      { to: "/master-admin/governance/flags", icon: Compass, label: "Feature Flags" },
      { to: "/master-admin/governance/audit", icon: FileText, label: "Audit Log" },
      { to: "/master-admin/governance/lifecycle", icon: Route, label: "Lifecycle Rules" },
      { to: "/master-admin/governance/scoring", icon: Target, label: "Scoring Engine" },
      { to: "/master-admin/governance/automation", icon: Kanban, label: "CRM Automation" },
    ],
  },
];

export const getAssociationAdminNavItems = (): NavItem[] => [
  { to: "/association", icon: Network, label: "Association Hub" },
  { to: "/support", icon: Headset, label: "Support" },
  { to: "/settings", icon: Wrench, label: "Settings" },
];
